/**
 * Flow Settings Types
 *
 * Base interface with common settings, extended by campaign-specific interfaces.
 */

/**
 * Period definition for time-based settings
 */
export interface FlowSettingsPeriod {
  unit: 'MINUTE' | 'HOUR' | 'DAY' | 'MONTH';
  value: number;
}

/**
 * Date range for programmed periods (LEGACY v1 — kept for retro-compatibility).
 *
 * Persisted inside `date_ranges` (a JSON string). Superseded by the advanced
 * scheduling model below (`FlowScheduleConfig`), but still written as a mirror
 * so a back-end that only understands v1 keeps enforcing the date part.
 */
export interface FlowSettingsDateRange {
  date_start: string;
  date_end: string;
  year_option?: 'current' | 'everyYear';
  inclusion_type?: 'include' | 'exclude';
}

/**
 * ─────────────────────────────────────────────────────────────────────────
 * Advanced scheduling (v2) — supersedes the flat `date_ranges` array.
 *
 * Stored as a JSON string in `schedule_config`. This shape is the FROZEN
 * CONTRACT shared with the NestJS back-end (see WORKFLOW_SCHEDULING_V2_PLAN):
 * the back mirrors the exact same evaluation logic (`evaluateSchedule`).
 *
 * A rule combines OPTIONAL constraints (date / weekdays / time) with an
 * effect. A moment matches a rule when every present constraint matches.
 * Absence of a constraint means "no restriction on that dimension".
 * ─────────────────────────────────────────────────────────────────────────
 */
export type FlowScheduleEffect = 'include' | 'exclude';

/** "once" = absolute dates carrying a year · "yearly" = recurring every year */
export type FlowScheduleDateMode = 'once' | 'yearly';

export interface FlowScheduleDate {
  mode: FlowScheduleDateMode;
  /**
   * "once": ISO `YYYY-MM-DD` (whole day) or `YYYY-MM-DDTHH:mm` (precise moment,
   * continuous window) · "yearly": `MM-DD` (whole day) or `MM-DDTHH:mm`.
   */
  start: string;
  /** Same format as {@link start}. */
  end: string;
}

export interface FlowScheduleTime {
  /** `HH:mm` (24h) */
  start: string;
  /** `HH:mm` (24h). If `end` <= `start` the window wraps past midnight. */
  end: string;
}

export interface FlowScheduleRule {
  /** Stable client-side id (list keys, dedup). Not interpreted by the back. */
  id: string;
  /** Optional free label for the UI. */
  label?: string;
  effect: FlowScheduleEffect;
  /** Optional date constraint. Absent/null = applies on every date. */
  date?: FlowScheduleDate | null;
  /** Optional ISO weekdays (1=Mon … 7=Sun). Absent/empty = all weekdays. */
  weekdays?: number[] | null;
  /** Optional time-of-day window. Absent/null = whole day (00:00–23:59). */
  time?: FlowScheduleTime | null;
}

/** Current schedule schema version. Bump only on a breaking shape change. */
export const FLOW_SCHEDULE_VERSION = 2;

export type FlowScheduleTimezoneMode = 'shop' | 'contact' | 'utc';

export interface FlowScheduleConfig {
  /** Schema version. `2` = this model; absent/1 ⇒ legacy `date_ranges` path. */
  version: number;
  /** Master on/off. Mirrors `program_date_ranges`. */
  enabled: boolean;
  /** Timezone reference for evaluation. Default `shop`. */
  timezone_mode: FlowScheduleTimezoneMode;
  rules: FlowScheduleRule[];
}

/**
 * Base settings interface - common to all campaign types
 */
export interface FlowSettingsBase {
  /** Campaign name */
  name: string;
}

/**
 * Automation campaign settings
 */
export interface FlowSettingsAutomation extends FlowSettingsBase {
  /** Optional campaign description/legend */
  legend?: string;

  /** Workflow type: marketing or transactional */
  type_workflow: 'marketing' | 'transactional';

  /** Whether the scenario triggers only once per visitor (1) or multiple times (0) */
  unique: 0 | 1;

  /** Type of uniqueness: per visitor (1) or per session (0) - only if unique === 1 */
  type_unique: 0 | 1 | null;

  /** Allow re-triggering after a period (1) or never (0) - only if unique === 1 && type_unique === 1 */
  type_retrigger: 0 | 1 | null;

  /** Period before re-triggering - only if type_retrigger === 1 */
  time_unit_select: FlowSettingsPeriod;

  /** Enable programmed date ranges (1) or not (0) */
  program_date_ranges: 0 | 1 | null;

  /** JSON string of date ranges (LEGACY v1) - only if program_date_ranges === 1 */
  date_ranges: string;

  /**
   * JSON string of the advanced schedule (v2, {@link FlowScheduleConfig}).
   * Empty/absent ⇒ none → the legacy `date_ranges` path is used. When present
   * it is the source of truth; `date_ranges` is kept as a best-effort mirror
   * for a back-end that only understands v1. Gated by `program_date_ranges`.
   */
  schedule_config?: string;

  /** Track sales attributed to this scenario (1) or not (0) */
  track_sales: 0 | 1;
}

/**
 * Bulk campaign settings
 */
export interface FlowSettingsBulk extends FlowSettingsBase {
  /** Optional campaign description/legend */
  legend?: string;

  /** Scheduled sending time (format: YYYYMMDDHHmmss) */
  time_sending: string;

  /** Use perfect timing optimization (1) or not (0) */
  perfect_timing: 0 | 1;

  /** Enable slowdown/spread sending over time (1) or not (0) - only if perfect_timing === 0 */
  slowdown: 0 | 1;

  /** Interval between sends in minutes - only if slowdown === 1 */
  interval_between_sends: '15' | '30' | '60' | '90' | '120' | '150' | '180' | '210' | '240' | '270' | '300';

  /** Whether campaign is commercial (1) or not (0) */
  commercial_campaign: 0 | 1;

  /** Track sales attributed to this campaign (1) or not (0) */
  track_sales: 0 | 1;
}

/**
 * Union type for all flow settings
 */
export type FlowSettings = FlowSettingsAutomation | FlowSettingsBulk;

/**
 * Type guard to check if settings are for automation
 */
export const isAutomationSettings = (settings: FlowSettings): settings is FlowSettingsAutomation => 'type_workflow' in settings;

/**
 * Type guard to check if settings are for bulk
 */
export const isBulkSettings = (settings: FlowSettings): settings is FlowSettingsBulk => 'commercial_campaign' in settings;

/**
 * Default settings for automation campaigns
 */
export const getDefaultAutomationSettings = (name = ''): FlowSettingsAutomation => ({
  name,
  legend: '',
  type_workflow: 'marketing',
  unique: 0,
  type_unique: 0,
  type_retrigger: 0,
  time_unit_select: { unit: 'DAY', value: 1 },
  program_date_ranges: 0,
  date_ranges: '[]',
  schedule_config: '',
  track_sales: 1,
});

/**
 * Default settings for bulk campaigns
 */
export const getDefaultBulkSettings = (name = ''): FlowSettingsBulk => ({
  name,
  legend: '',
  time_sending: '',
  perfect_timing: 0,
  slowdown: 0,
  interval_between_sends: '15',
  commercial_campaign: 1,
  track_sales: 1,
});

/**
 * Idempotent coercion of flow settings — converts legacy string `"0"`/`"1"`
 * binaries (PHP origin) to numbers, normalises `time_unit_select.value` to a
 * number, falls back from `trigger_time`/`unit_time` if `time_unit_select`
 * is missing, and reads `type_retriggered` (persisted key) as a fallback for
 * `type_retrigger` (internal key). Already-correct settings pass through.
 *
 * Centralised so every entry into the store (action transform, localStorage
 * restore, viewer dispatch, version-conflict resolution) gets normalised at
 * the same chokepoint — see `setFlow` in `store/modules/flow-editor.ts`.
 */
export const coerceFlowSettings = <T extends FlowSettings>(settings: T): T => {
  const toBinary = (v: unknown, fallback: 0 | 1): 0 | 1 => {
    if (v == null) return fallback;
    return Number(v) === 1 ? 1 : 0;
  };
  const toBinaryNullable = (
    v: unknown,
    fallback: 0 | 1 | null,
  ): 0 | 1 | null => {
    if (v == null) return fallback;
    return Number(v) === 1 ? 1 : 0;
  };

  if (isAutomationSettings(settings)) {
    const s = settings as FlowSettingsAutomation
      & { trigger_time?: unknown; unit_time?: unknown; type_retriggered?: unknown };
    const rawRetrigger = s.type_retriggered ?? s.type_retrigger;
    const triggerTime = s.time_unit_select?.value ?? s.trigger_time;
    const unitTime = s.time_unit_select?.unit ?? s.unit_time;
    return {
      ...s,
      unique: toBinary(s.unique, 0),
      type_unique: toBinaryNullable(s.type_unique, 0),
      type_retrigger: toBinaryNullable(rawRetrigger, 0),
      time_unit_select: {
        unit: (unitTime as FlowSettingsPeriod['unit']) ?? 'DAY',
        value: Number(triggerTime) || 1,
      },
      program_date_ranges: toBinaryNullable(s.program_date_ranges, 0),
    } as T;
  }
  if (isBulkSettings(settings)) {
    const s = settings as FlowSettingsBulk;
    return {
      ...s,
      perfect_timing: toBinary(s.perfect_timing, 0),
      slowdown: toBinary(s.slowdown, 0),
      commercial_campaign: toBinary(s.commercial_campaign, 1),
    } as T;
  }
  return settings;
};
